import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { api } from '../lib/api';

const card = { boxShadow: '0px 2px 8px 0px rgba(0,0,0,0.04)' };
const INPUT = 'w-full px-3 py-2 text-[13px] border border-[#e5e5e5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3fb5d2] focus:border-transparent text-[#3b3b3b] placeholder-[#c0c0c0] bg-white';
const LABEL = 'block text-[12px] font-medium text-[#646464] mb-1';
const API_BASE = process.env.REACT_APP_API_URL || window.location.origin;

const SectionCard = ({ title, desc, children }) => (
  <div className="bg-white rounded-2xl p-6" style={card}>
    <h2 className="text-[15px] font-semibold text-[#3b3b3b] mb-1">{title}</h2>
    {desc && <p className="text-[13px] text-[#8a8a8a] mb-5">{desc}</p>}
    {children}
  </div>
);

const SaveButton = ({ onClick, saving, label = 'Simpan', loadingLabel = 'Menyimpan...' }) => (
  <button onClick={onClick} disabled={saving}
    className="px-4 py-2 text-[13px] font-semibold bg-[#3fb5d2] hover:bg-[#2a798c] text-white rounded-lg transition-colors disabled:opacity-50">
    {saving ? loadingLabel : label}
  </button>
);

const Settings = () => {
  const location = useLocation();

  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [syncMethod, setSyncMethod]               = useState('puppeteer');
  const [autoApprovePostLink, setAutoApprovePostLink] = useState(false);
  const [cronToken, setCronToken]                 = useState('');
  const [generatingToken, setGeneratingToken]     = useState(false);
  const [copied, setCopied]                       = useState(false);
  const [xenditConfig, setXenditConfig]           = useState({ environment: 'sandbox', apiKey: '' });

  // Google OAuth
  const [googleOAuthCreds, setGoogleOAuthCreds]   = useState({ clientId: '', clientSecret: '' });
  const [googleStatus, setGoogleStatus]           = useState({ hasOAuth: false, email: '', hasServiceAccount: false, hasOAuthCreds: false });
  const [connectingGoogle, setConnectingGoogle]   = useState(false);
  const [disconnectingGoogle, setDisconnectingGoogle] = useState(false);
  const [googleTab, setGoogleTab]                 = useState('oauth'); // 'oauth' | 'service_account'

  // Service Account
  const [googleServiceAccountJson, setGoogleServiceAccountJson] = useState('');

  // Handle OAuth callback result in URL query
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const googleResult = params.get('google');
    if (googleResult === 'connected') {
      alert('Akun Google berhasil dihubungkan!');
      window.history.replaceState({}, '', '/settings');
      loadGoogleStatus();
    } else if (googleResult === 'error') {
      const msg = params.get('msg') || 'Terjadi kesalahan';
      alert('Gagal menghubungkan Google: ' + msg);
      window.history.replaceState({}, '', '/settings');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for postMessage from OAuth popup
  useEffect(() => {
    const handler = (e) => {
      if (e.data === 'google-connected') {
        alert('Akun Google berhasil dihubungkan!');
        loadGoogleStatus();
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadSettings(); }, []);

  const loadGoogleStatus = useCallback(async () => {
    try {
      const status = await api.get('/api/google/status');
      setGoogleStatus(status);
    } catch { /* ignore */ }
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const [settings] = await Promise.all([api.get('/api/settings')]);
      setSyncMethod(settings.find(s => s.key === 'sync_method')?.value || 'puppeteer');
      setAutoApprovePostLink(settings.find(s => s.key === 'auto_approve_post_link')?.value === 'true');
      setCronToken(settings.find(s => s.key === 'cron_sync_token')?.value || '');
      setXenditConfig({
        environment: settings.find(s => s.key === 'xendit_environment')?.value || 'sandbox',
        apiKey:      settings.find(s => s.key === 'xendit_api_key')?.value      || '',
      });
      setGoogleOAuthCreds({
        clientId:     settings.find(s => s.key === 'google_oauth_client_id')?.value     || '',
        clientSecret: settings.find(s => s.key === 'google_oauth_client_secret')?.value || '',
      });
      setGoogleServiceAccountJson(settings.find(s => s.key === 'google_service_account_json')?.value || '');
    } catch (error) { alert('Gagal memuat settings: ' + error.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (!loading) loadGoogleStatus(); }, [loading, loadGoogleStatus]);

  const handleGenerateCronToken = async () => {
    if (cronToken && !window.confirm('Token lama akan tidak berlaku. Lanjutkan?')) return;
    try {
      setGeneratingToken(true);
      const result = await api.post('/api/settings/generate-cron-token', {});
      setCronToken(result.token);
    } catch (error) { alert('Gagal generate token: ' + error.message); }
    finally { setGeneratingToken(false); }
  };

  const cronUrl = cronToken ? `${API_BASE}/api/cron/sync-all-campaigns?token=${cronToken}` : '';

  const handleCopyCronUrl = () => {
    if (!cronUrl) return;
    navigator.clipboard.writeText(cronUrl).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };

  const handleSave = async (key, value, desc) => {
    try {
      setSaving(true);
      await api.put(`/api/settings/${key}`, { value, description: desc });
      alert('Settings berhasil disimpan');
    } catch (error) { alert('Gagal menyimpan: ' + error.message); }
    finally { setSaving(false); }
  };

  const handleSaveOAuthCreds = async () => {
    if (!googleOAuthCreds.clientId.trim() || !googleOAuthCreds.clientSecret.trim()) {
      return alert('Client ID dan Client Secret wajib diisi');
    }
    try {
      setSaving(true);
      await api.put('/api/settings/google_oauth_client_id', { value: googleOAuthCreds.clientId.trim(), description: 'Google OAuth Client ID' });
      await api.put('/api/settings/google_oauth_client_secret', { value: googleOAuthCreds.clientSecret.trim(), description: 'Google OAuth Client Secret' });
      await loadGoogleStatus();
      alert('Credentials disimpan. Klik "Hubungkan Akun Google" untuk melanjutkan.');
    } catch (error) { alert('Gagal menyimpan: ' + error.message); }
    finally { setSaving(false); }
  };

  const handleConnectGoogle = async () => {
    try {
      setConnectingGoogle(true);
      const result = await api.get('/api/google/auth-url');
      const popup = window.open(result.url, 'google-auth', 'width=600,height=700,left=200,top=100');
      if (!popup) {
        // Popup blocked — fallback to redirect
        window.location.href = result.url;
      }
    } catch (error) {
      alert('Gagal membuat URL OAuth: ' + (error.response?.data?.error || error.message));
    } finally { setConnectingGoogle(false); }
  };

  const handleDisconnectGoogle = async () => {
    if (!window.confirm('Putuskan koneksi akun Google?')) return;
    try {
      setDisconnectingGoogle(true);
      await api.delete('/api/google/disconnect');
      await loadGoogleStatus();
    } catch (error) { alert('Gagal disconnect: ' + error.message); }
    finally { setDisconnectingGoogle(false); }
  };

  const handleSaveServiceAccount = async () => {
    if (!googleServiceAccountJson.trim()) return alert('Masukkan Service Account JSON terlebih dahulu');
    try { JSON.parse(googleServiceAccountJson); }
    catch { return alert('Format JSON tidak valid.'); }
    try {
      setSaving(true);
      await api.put('/api/settings/google_service_account_json', {
        value: googleServiceAccountJson.trim(),
        description: 'Google Service Account JSON untuk export ke Google Sheets',
      });
      await loadGoogleStatus();
      alert('Service Account berhasil disimpan');
    } catch (error) { alert('Gagal menyimpan: ' + error.message); }
    finally { setSaving(false); }
  };

  const handleSaveXendit = async () => {
    try {
      setSaving(true);
      await api.put('/api/settings/xendit_environment', { value: xenditConfig.environment, description: 'Environment Xendit: sandbox atau production' });
      await api.put('/api/settings/xendit_api_key',      { value: xenditConfig.apiKey,      description: 'API Key untuk autentikasi Xendit API' });
      alert('Konfigurasi Xendit berhasil disimpan');
    } catch (error) { alert('Gagal menyimpan: ' + error.message); }
    finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#3fb5d2] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const radioClass = (selected) =>
    `flex items-start gap-3 p-4 border rounded-xl cursor-pointer transition-colors ${selected ? 'border-[#3fb5d2] bg-[#ecf8fb]' : 'border-[#e5e5e5] hover:bg-gray-50'}`;

  const tabClass = (active) =>
    `px-4 py-2 text-[13px] font-medium rounded-lg transition-colors ${active ? 'bg-[#3fb5d2] text-white' : 'bg-[#f5f5f5] text-[#646464] hover:bg-[#e5e5e5]'}`;

  return (
    <div className="space-y-4">
      <h1 className="text-[22px] font-semibold text-[#3b3b3b] leading-8">Settings</h1>

      {/* Sync Method */}
      <SectionCard title="Sync Metrics Settings" desc="Pilih metode sync metrics dari social media.">
        <div className="space-y-3 mb-5">
          {[
            { value: 'puppeteer', title: 'Puppeteer (Default)', desc: 'Browser automation. Gratis tapi mungkin terblokir WAF TikTok.' },
            { value: 'paid_api',  title: 'Paid API',            desc: 'Lebih reliable dan tidak terblokir. Memerlukan biaya.' },
          ].map(({ value, title, desc }) => (
            <label key={value} className={radioClass(syncMethod === value)}>
              <input type="radio" name="syncMethod" value={value} checked={syncMethod === value}
                onChange={(e) => setSyncMethod(e.target.value)}
                className="mt-0.5 h-4 w-4 text-[#3fb5d2] border-[#e5e5e5] focus:ring-[#3fb5d2]" />
              <div>
                <p className="text-[13px] font-semibold text-[#3b3b3b]">{title}</p>
                <p className="text-[12px] text-[#8a8a8a] mt-0.5">{desc}</p>
              </div>
            </label>
          ))}
        </div>
        <SaveButton onClick={() => handleSave('sync_method', syncMethod, 'Metode sync metrics')} saving={saving} />
      </SectionCard>

      {/* Auto Approve */}
      <SectionCard title="Auto Approve Post Link" desc="Bila aktif, post link dari hunter langsung ter-verify tanpa perlu approval manual.">
        <label className={radioClass(false) + ' mb-5'}>
          <input type="checkbox" checked={autoApprovePostLink} onChange={(e) => setAutoApprovePostLink(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded text-[#3fb5d2] border-[#e5e5e5] focus:ring-[#3fb5d2]" />
          <div>
            <p className="text-[13px] font-semibold text-[#3b3b3b]">Aktifkan Auto Approve</p>
            <p className="text-[12px] text-[#8a8a8a] mt-0.5">Post link langsung monitored tanpa perlu approve manual.</p>
          </div>
        </label>
        <SaveButton onClick={() => handleSave('auto_approve_post_link', autoApprovePostLink ? 'true' : 'false', 'Auto approve post link')} saving={saving} />
      </SectionCard>

      {/* Google Sheets Export */}
      <SectionCard title="Google Sheets Export" desc="Konfigurasi autentikasi Google untuk export konten campaign ke Google Sheets.">

        {/* Connection status banner */}
        {googleStatus.hasOAuth ? (
          <div className="flex items-center justify-between gap-3 mb-5 p-3 bg-green-50 border border-green-200 rounded-xl">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />
              <p className="text-[13px] text-green-800 font-medium">
                Terhubung via OAuth{googleStatus.email ? ` · ${googleStatus.email}` : ''}
              </p>
            </div>
            <button
              onClick={handleDisconnectGoogle}
              disabled={disconnectingGoogle}
              className="text-[12px] text-red-600 hover:text-red-800 font-medium underline underline-offset-2 disabled:opacity-50 flex-shrink-0">
              {disconnectingGoogle ? 'Memutus...' : 'Putuskan'}
            </button>
          </div>
        ) : googleStatus.hasServiceAccount ? (
          <div className="flex items-center gap-2 mb-5 p-3 bg-blue-50 border border-blue-200 rounded-xl">
            <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
            <p className="text-[13px] text-blue-800 font-medium">Terhubung via Service Account</p>
          </div>
        ) : (
          <div className="flex items-center gap-2 mb-5 p-3 bg-amber-50 border border-amber-200 rounded-xl">
            <span className="w-2 h-2 rounded-full bg-amber-400 flex-shrink-0" />
            <p className="text-[13px] text-amber-800">Belum terhubung — pilih metode di bawah</p>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-5">
          <button className={tabClass(googleTab === 'oauth')} onClick={() => setGoogleTab('oauth')}>OAuth (Disarankan)</button>
          <button className={tabClass(googleTab === 'service_account')} onClick={() => setGoogleTab('service_account')}>Service Account</button>
        </div>

        {/* OAuth tab */}
        {googleTab === 'oauth' && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={LABEL}>Client ID</label>
                <input type="text" value={googleOAuthCreds.clientId}
                  onChange={(e) => setGoogleOAuthCreds(p => ({ ...p, clientId: e.target.value }))}
                  className={INPUT} placeholder="xxxxx.apps.googleusercontent.com" />
              </div>
              <div>
                <label className={LABEL}>Client Secret</label>
                <input type="password" value={googleOAuthCreds.clientSecret}
                  onChange={(e) => setGoogleOAuthCreds(p => ({ ...p, clientSecret: e.target.value }))}
                  className={INPUT} placeholder="GOCSPX-..." />
              </div>
            </div>
            <p className="text-[11px] text-[#8a8a8a]">
              Buat OAuth 2.0 Client ID di <strong>Google Cloud Console</strong> (tipe: Web Application) → aktifkan Google Sheets API & Drive API → tambahkan
              <code className="mx-1 px-1 bg-gray-100 rounded text-[10px]">{API_BASE}/api/google/callback</code>
              ke Authorized Redirect URIs.
            </p>
            <div className="flex gap-2 flex-wrap">
              <SaveButton onClick={handleSaveOAuthCreds} saving={saving} label="Simpan Credentials" />
              {googleStatus.hasOAuthCreds && !googleStatus.hasOAuth && (
                <button
                  onClick={handleConnectGoogle}
                  disabled={connectingGoogle}
                  className="px-4 py-2 text-[13px] font-semibold bg-[#0f9d58] hover:bg-[#0b7a45] text-white rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2">
                  {connectingGoogle
                    ? <><span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />Membuka...</>
                    : 'Hubungkan Akun Google'}
                </button>
              )}
              {googleStatus.hasOAuth && (
                <button
                  onClick={handleConnectGoogle}
                  disabled={connectingGoogle}
                  className="px-4 py-2 text-[13px] font-medium border border-[#e5e5e5] text-[#646464] hover:bg-gray-50 rounded-lg transition-colors disabled:opacity-50">
                  {connectingGoogle ? 'Membuka...' : 'Ganti Akun'}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Service Account tab */}
        {googleTab === 'service_account' && (
          <div className="space-y-4">
            <div>
              <label className={LABEL}>Service Account JSON</label>
              <textarea
                value={googleServiceAccountJson}
                onChange={(e) => setGoogleServiceAccountJson(e.target.value)}
                rows={6}
                placeholder={'Paste isi file JSON Service Account Google di sini...\n{\n  "type": "service_account",\n  "project_id": "...",\n  ...\n}'}
                className={INPUT + ' font-mono text-[11px] resize-y'}
              />
              <p className="text-[11px] text-[#8a8a8a] mt-1">
                Buat Service Account di <strong>Google Cloud Console</strong> → aktifkan Google Sheets API & Drive API → download file JSON credentials → paste di sini.
              </p>
            </div>
            <SaveButton onClick={handleSaveServiceAccount} saving={saving} label="Simpan Service Account" />
          </div>
        )}
      </SectionCard>

      {/* Xendit */}
      <SectionCard title="Konfigurasi Xendit" desc="Integrasi Xendit API untuk pembayaran hunter.">
        <div className="space-y-4 mb-5">
          <div>
            <label className={LABEL}>Environment</label>
            <select name="environment" value={xenditConfig.environment} onChange={(e) => setXenditConfig(p => ({ ...p, environment: e.target.value }))} className={INPUT}>
              <option value="sandbox">Sandbox (Testing)</option>
              <option value="production">Production</option>
            </select>
            <p className="text-[11px] text-[#8a8a8a] mt-1">Gunakan Sandbox untuk testing, Production untuk transaksi real.</p>
          </div>
          <div>
            <label className={LABEL}>API Key</label>
            <input type="password" name="apiKey" value={xenditConfig.apiKey}
              onChange={(e) => setXenditConfig(p => ({ ...p, apiKey: e.target.value }))}
              className={INPUT} placeholder="Masukkan API Key" />
          </div>
        </div>
        <SaveButton onClick={handleSaveXendit} saving={saving} label="Simpan Konfigurasi Xendit" />
      </SectionCard>

      {/* Cron Sync */}
      <SectionCard title="Auto Sync via Cron" desc="Gunakan URL di bawah sebagai cron job untuk otomatis sync semua campaign aktif.">
        <div className="space-y-4">
          {cronToken ? (
            <div>
              <label className={LABEL}>Cron URL</label>
              <div className="flex gap-2">
                <input type="text" readOnly value={cronUrl}
                  className={INPUT + ' font-mono text-[11px] bg-[#f5f5f5]'} />
                <button onClick={handleCopyCronUrl}
                  className="flex-shrink-0 px-3 py-2 text-[12px] font-medium text-[#3b3b3b] border border-[#e5e5e5] rounded-lg bg-white hover:bg-gray-50 transition-colors whitespace-nowrap">
                  {copied ? '✓ Tersalin' : 'Copy'}
                </button>
              </div>
              <p className="text-[11px] text-[#8a8a8a] mt-1">Panggil dengan GET dari cron service (cron-job.org, EasyCron, dll).</p>
            </div>
          ) : (
            <div className="border border-dashed border-[#e5e5e5] rounded-xl p-4 text-center text-[13px] text-[#8a8a8a]">
              Belum ada token. Klik "Generate Token" untuk membuat URL cron.
            </div>
          )}
          <button onClick={handleGenerateCronToken} disabled={generatingToken}
            className="px-4 py-2 text-[13px] font-semibold bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-colors disabled:opacity-50">
            {generatingToken ? 'Generating...' : cronToken ? 'Regenerate Token' : 'Generate Token'}
          </button>
        </div>

        <div className="mt-5 bg-[#ecf8fb] border border-[#3fb5d2]/20 rounded-xl p-4">
          <p className="text-[12px] font-semibold text-[#2a798c] mb-2">Cara Penggunaan Cron:</p>
          <ul className="text-[12px] text-[#2a798c]/80 space-y-1 list-disc list-inside">
            <li>Copy URL di atas dan daftarkan ke cron service pilihan Anda</li>
            <li>Interval minimal 1 jam agar tidak membebani server</li>
            <li>Jika token bocor, klik "Regenerate Token" untuk invalidate URL lama</li>
            <li>Sync hanya berjalan untuk campaign dengan status <strong>active</strong></li>
          </ul>
        </div>
      </SectionCard>
    </div>
  );
};

export default Settings;
